import type { SignatureEligibilityPolicy, SigningMode } from "@closer/shared";
import {
  SIGNATURE_ELIGIBILITY_POLICIES,
  SIGNATURE_ELIGIBILITY_POLICY_NAMES,
  SIGNING_MODE_NAMES,
  SIGNING_MODES,
} from "@closer/shared";
import { Button, FormField, Input, Modal, Select, Skeleton, useToast } from "@closer/ui";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { api } from "../../lib/apiClient";
import { useAuth } from "../../lib/authContext";
import { sozlesmeSurumSorgusu } from "../../lib/sharedQueries";

/**
 * İMZA SÜRECİ BAŞLATMA (CLOS-164 `[157]`).
 *
 * ### ⚠ Talep DEĞİŞMEZ VERSİYONA çakılıyor
 *
 * Kullanıcı hangi sürümün imzalanacağını açıkça seçiyor; "güncel metin" diye bir seçenek
 * yok. Sözleşme sonradan değişse bile imzalanan metin sabit kalıyor — kartın ilk kriteri.
 *
 * ### ⚠ Uygunluk politikası SEÇİM, varsayılan değil
 *
 * Politika talebin üzerine yazılıyor ve sonradan konfig değişse de o talebin hangi kurala
 * dayandığı okunabiliyor. Sunucu seçimi konfigdeki izinli listeyle süzüyor; ekran seçeneği
 * sunuyor, kararı vermiyor.
 */
export function ContractSignatureRequestDialog({
  open,
  contractId,
  onClose,
  onDone,
}: {
  open: boolean;
  contractId: string;
  onClose: () => void;
  onDone: () => Promise<void> | void;
}) {
  const { activeTenantId } = useAuth();
  const tenantId = activeTenantId!;
  const toast = useToast();

  const [versionId, setVersionId] = useState("");
  const [policy, setPolicy] = useState<SignatureEligibilityPolicy>("NONE");
  const [mode, setMode] = useState<SigningMode>("parallel");
  const [expiresAt, setExpiresAt] = useState("");

  const versiyonlar = useQuery({
    enabled: open,
    ...sozlesmeSurumSorgusu(tenantId, contractId),
  });

  useEffect(() => {
    if (!open) return;
    setPolicy("NONE");
    setMode("parallel");
    setExpiresAt("");
    /** En güncel sürüm ön seçili: kullanıcı çoğu zaman onu imzalatıyor, ama değiştirebiliyor. */
    setVersionId(versiyonlar.data?.[0]?.id ?? "");
  }, [open, versiyonlar.data]);

  const olustur = useMutation({
    mutationFn: async () =>
      api(`/api/contracts/${contractId}/signature-requests`, {
        method: "POST",
        tenantId,
        body: JSON.stringify({
          versionId,
          eligibilityPolicy: policy,
          signingMode: mode,
          /** ⚠ İmza süresi ZORUNLU (ADR-0065); düğme boş tarihte kapalı olduğu için HER ZAMAN gider. */
          expiresAt: new Date(`${expiresAt}T23:59:59`).toISOString(),
        }),
      }),
    onSuccess: async () => {
      toast.success("İmza süreci başlatıldı");
      await onDone();
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : "İmza süreci başlatılamadı"),
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="İmza süreci başlat"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Vazgeç
          </Button>
          <Button
            onClick={() => olustur.mutate()}
            loading={olustur.isPending}
            disabled={versionId === "" || expiresAt === ""}
          >
            Başlat
          </Button>
        </>
      }
    >
      {versiyonlar.isPending ? (
        <div role="status" aria-label="Sürümler yükleniyor">
          <Skeleton lines={3} />
        </div>
      ) : (versiyonlar.data ?? []).length === 0 ? (
        <p className="text-sm text-fg-muted">
          İmza süreci için önce bir sürüm oluşturmalısınız; imza değişmez bir metne bağlanır.
        </p>
      ) : (
        <div className="flex flex-col gap-3">
          <FormField label="İmzalanacak sürüm">
            <Select
              id="imza-versiyon"
              value={versionId}
              onChange={(olay) => setVersionId(olay.target.value)}
            >
              {(versiyonlar.data ?? []).map((v) => (
                <option key={v.id} value={v.id}>
                  Sürüm {v.versionNumber} · {new Date(v.createdAt).toLocaleDateString("tr-TR")}
                </option>
              ))}
            </Select>
          </FormField>

          <FormField label="Ön koşul politikası">
            <Select
              id="imza-politika"
              value={policy}
              onChange={(olay) => setPolicy(olay.target.value as SignatureEligibilityPolicy)}
            >
              {SIGNATURE_ELIGIBILITY_POLICIES.map((p) => (
                <option key={p} value={p}>
                  {SIGNATURE_ELIGIBILITY_POLICY_NAMES[p]}
                </option>
              ))}
            </Select>
          </FormField>

          <FormField label="İmza sırası">
            <Select
              id="imza-sira"
              value={mode}
              onChange={(olay) => setMode(olay.target.value as SigningMode)}
            >
              {SIGNING_MODES.map((m) => (
                <option key={m} value={m}>
                  {SIGNING_MODE_NAMES[m]}
                </option>
              ))}
            </Select>
          </FormField>

          <FormField
            label="İmza süresi"
            required
            hint="Süreç başlatılırken son tarih seçilmelidir (ADR-0065)."
          >
            <Input
              id="imza-sure"
              type="date"
              required
              value={expiresAt}
              onChange={(olay) => setExpiresAt(olay.target.value)}
            />
          </FormField>
        </div>
      )}
    </Modal>
  );
}
